/**
 * Pure transforms for the coin-economy charts on the Analytics page.
 *
 * Coins are displayed exactly as the API reports them (`paidCoins`,
 * `bonusCoins`, `coinsSold`) — never derived as paise / coin rate, because
 * package prices and coins are independent and there is no fixed ratio.
 */

export interface CoinSalesBucket {
  /** Contract name; older API builds send `bucket`. */
  day?: string;
  bucket?: string;
  paidCoins?: number | null;
  bonusCoins?: number | null;
  cashPaise?: string | null;
}

export interface GiftRevenueRow {
  giftCode: string;
  giftName: string;
  sends: number;
  totalPaise: string;
  /** Coins spent on this gift, when the API reports it. */
  totalCoins?: number | null;
}

export interface CoinSalesPoint {
  day: string;
  kind: 'paid coins' | 'bonus coins';
  coins: number;
}

export function coinSalesColumnData(
  rows: CoinSalesBucket[],
  dayLabel: (iso: string) => string,
): CoinSalesPoint[] {
  return rows.flatMap((b) => {
    const iso = b.day ?? b.bucket;
    if (!iso) return [];
    const day = dayLabel(iso);
    return [
      { day, kind: 'paid coins' as const, coins: Number(b.paidCoins ?? 0) },
      { day, kind: 'bonus coins' as const, coins: Number(b.bonusCoins ?? 0) },
    ];
  });
}

export interface GiftPieData {
  unit: 'coins' | '₹';
  data: Array<{ gift: string; value: number }>;
}

/**
 * Gift revenue slices. Uses the reported coin totals when every row has one;
 * otherwise falls back to the ₹ value of `totalPaise` — never mixes units and
 * never converts paise into coins.
 */
export function giftPieData(rows: GiftRevenueRow[]): GiftPieData {
  const allCoins = rows.length > 0 && rows.every((g) => typeof g.totalCoins === 'number');
  if (allCoins) {
    return {
      unit: 'coins',
      data: rows.map((g) => ({ gift: g.giftName, value: g.totalCoins as number })),
    };
  }
  return {
    unit: '₹',
    data: rows.map((g) => ({ gift: g.giftName, value: Number(g.totalPaise ?? 0) / 100 })),
  };
}
